import { useBranding } from "../../../../branding/BrandingProvider";
import { Tooltip } from "../../../../components/ui/Tooltip";

interface BackgroundSwatchesProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
}

interface SwatchDef {
  key: string;
  label: string;
}

interface SwatchGroup {
  group: string;
  swatches: SwatchDef[];
}

const SWATCH_GROUPS: SwatchGroup[] = [
  {
    group: "Brand colors",
    swatches: [
      { key: "primary", label: "Primary" },
      { key: "secondary", label: "Secondary" },
      { key: "tertiary", label: "Tertiary" },
      { key: "brand", label: "Brand" },
    ],
  },
  {
    group: "Backgrounds",
    swatches: [
      { key: "primaryBg", label: "Primary BG" },
      { key: "secondaryBg", label: "Secondary BG" },
      { key: "tertiaryBg", label: "Tertiary BG" },
      { key: "surface", label: "Surface" },
    ],
  },
  {
    group: "Neutrals",
    swatches: [
      { key: "dark", label: "Dark" },
      { key: "light", label: "Light" },
    ],
  },
];

/**
 * Named swatches for section background style, organized into
 * labeled groups for comprehensibility.
 */
export function BackgroundSwatches({
  value,
  onChange,
  label = "Background",
}: BackgroundSwatchesProps) {
  const { branding } = useBranding();

  function colorFor(key: string): string {
    switch (key) {
      case "primary":
        return branding?.primaryColor || "#b83333";
      case "secondary":
        return branding?.secondaryColor || "#2E8B8B";
      case "tertiary":
        return branding?.tertiaryColor || "#1e3a5f";
      case "surface":
        return "#f5f5f5";
      case "brand":
        return branding?.backgroundColor || "#196891";
      case "light":
        return "#ffffff";
      case "dark":
        return "#2c2c2c";
      case "primaryBg":
        return branding?.primaryBgColor || branding?.backgroundColor || "#196891";
      case "secondaryBg":
        return branding?.secondaryBgColor || "#f8f9fa";
      case "tertiaryBg":
        return branding?.tertiaryBgColor || "#f0f4f8";
      default:
        return "#888";
    }
  }

  return (
    <div className="mb-[18px]">
      <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
        {label}
      </label>

      {SWATCH_GROUPS.map(({ group, swatches }) => (
        <div key={group} className="mb-2.5 last:mb-0">
          <span className="block text-[9.5px] font-semibold text-ink-5 mb-1.5 tracking-wide">
            {group}
          </span>
          <div className="flex flex-wrap gap-2">
            {swatches.map(({ key, label }) => {
              const isActive = value === key;
              const color = colorFor(key);
              const isLight =
                key === "light" ||
                key === "surface" ||
                key === "secondaryBg" ||
                key === "tertiaryBg";

              return (
                <Tooltip key={key} label={`${label} • ${color}`} side="top">
                  <button
                    type="button"
                    onClick={() => onChange(key)}
                    className="bg-transparent border-0 p-0 cursor-pointer"
                  >
                    <span
                      className="block w-[34px] h-[34px] rounded-[9px] transition-all duration-100 relative"
                      style={{
                        backgroundColor: color,
                        border: isActive
                          ? "2px solid var(--color-tide)"
                          : isLight
                            ? "2px solid var(--color-ink-6)"
                            : "2px solid transparent",
                        boxShadow: isActive
                          ? "0 0 0 3px var(--color-tide-s)"
                          : undefined,
                      }}
                    >
                      {/* Active checkmark -- clear indicator for all colors */}
                      {isActive && (
                        <span className="absolute inset-0 flex items-center justify-center">
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                            className={isLight ? "text-tide" : "text-white"}
                          >
                            <path
                              d="M3 7.5L5.5 10L11 4"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                      )}
                    </span>
                  </button>
                </Tooltip>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
